/**
 * Shared reference preview renderer for post cards.
 * Prefers social embeds, then explicit reference images, then generic page thumbnails.
 */
import React from "react";
import {
  FacebookEmbed,
  InstagramEmbed,
  TikTokEmbed,
  XEmbed,
  YouTubeEmbed,
} from "react-social-media-embed";
import EntityThumbnail from "components/Entities/EntityThumbnail";

const embedProps = {
  className: "w-full flex-shrink-[2] md:max-w-[300px] md:max-h-[150px]",
  containerClassName: "relative w-full h-full [&>iframe]:w-fit [&>iframe]:h-fit",
};

/**
 * Returns a social embed when the URL belongs to a supported provider.
 */
export function renderSocialMediaEmbed(url?: string | null): React.ReactNode {
  if (!url) {
    return null;
  }

  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    return (
      <div className={embedProps.containerClassName}>
        <YouTubeEmbed url={url} youTubeProps={{ iframeClassName: embedProps.className }} />
      </div>
    );
  }

  if (url.includes("tiktok.com")) {
    return (
      <div className={`${embedProps.containerClassName} object-scale-down`}>
        <TikTokEmbed url={url} className={embedProps.className} />
      </div>
    );
  }

  if (url.includes("instagram.com")) {
    return (
      <div className={`${embedProps.containerClassName} object-scale-down`}>
        <InstagramEmbed
          url={url}
          className="w-full max-h-full md:max-w-[300px] md:max-h-[150px] object-scale-down"
        />
      </div>
    );
  }

  if (url.includes("x.com")) {
    return (
      <div className="object-scale-down">
        <XEmbed url={url} height={600} />
      </div>
    );
  }

  if (url.includes("twitter.com")) {
    return <XEmbed url={url} className={embedProps.className} />;
  }

  if (url.includes("facebook.com")) {
    return (
      <div className={`${embedProps.containerClassName} object-scale-down`}>
        <FacebookEmbed url={url} className={embedProps.className} />
      </div>
    );
  }

  return null;
}

export interface PostMediaPreviewProps {
  url?: string | null;
  referenceImageKey?: string | null;
  referenceImageUrl?: string | null;
  imageCdn?: string;
  isProfilePage?: boolean;
  onCardClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const PostMediaPreview: React.FC<PostMediaPreviewProps> = ({
  url,
  referenceImageKey,
  referenceImageUrl,
  imageCdn = "",
  isProfilePage = false,
  onCardClick,
}) => {
  const socialEmbed = renderSocialMediaEmbed(url);
  if (socialEmbed) {
    return <div className="social-embed-container relative w-full h-full overflow-hidden">{socialEmbed}</div>;
  }

  const explicitImageUrl =
    typeof referenceImageKey === "string" && referenceImageKey.length > 0
      ? `${imageCdn}/${referenceImageKey}`
      : referenceImageUrl ?? undefined;

  if (explicitImageUrl) {
    return (
      <EntityThumbnail
        url={explicitImageUrl}
        urlType="img"
        isProfilePage={isProfilePage}
        referenceLink={url ?? undefined}
        onCardClick={onCardClick}
      />
    );
  }

  if (url) {
    return (
      <EntityThumbnail
        url={url}
        urlType="page"
        isProfilePage={isProfilePage}
        referenceLink={url}
        onCardClick={onCardClick}
      />
    );
  }

  return null;
};

export default PostMediaPreview;
